import { useEffect, useState } from "react";
import { Loader2, Mail, Save, Send } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { api, admin, errMsg } from "@/lib/api";

const LABELS = {
  login: "Auto-login succeeded", autologin_failed: "Auto-login failed", auth_required: "Manual login required",
  stream_down: "Stream down", stream_restart: "Stream restarted", keys_updated: "Zerodha keys changed",
  keys_cleared: "Zerodha keys removed", api_key_rotated: "Master API key rotated",
};

export const NotificationsTab = ({ pw }) => {
  const [st, setSt] = useState(null);
  const [f, setF] = useState({ resend_key: "", from_domain: "", alert_email: "" });
  const [kinds, setKinds] = useState([]);
  const [busy, setBusy] = useState("");

  const load = (s) => {
    setSt(s);
    setF({ resend_key: "", from_domain: s.from_domain || "", alert_email: s.alert_email || "" });
    setKinds(s.alert_kinds || s.available_kinds);
  };
  useEffect(() => {
    api.get("/admin/notifications", admin(pw)).then((r) => load(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [pw]);

  const run = async (name, fn) => {
    setBusy(name);
    try {
      await fn();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const save = (enabled) => run("save", async () => {
    const body = { from_domain: f.from_domain, alert_email: f.alert_email, alert_kinds: kinds, enabled };
    if (f.resend_key) body.resend_key = f.resend_key;
    const { data } = await api.put("/admin/notifications", body, admin(pw));
    load(data.status);
    setF((x) => ({ ...x, resend_key: "" }));
    toast.success("Email alert settings saved");
  });
  const test = () => run("test", async () => {
    await save(true);
    const { data } = await api.post("/admin/notifications/test", null, admin(pw));
    toast.success(`Test email sent (id ${data.id})`);
  });
  const toggleKind = (k) => setKinds((ks) => (ks.includes(k) ? ks.filter((x) => x !== k) : [...ks, k]));

  if (!st) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-500" /></div>;
  return (
    <div className="space-y-4 pt-3" data-testid="notifications-tab">
      <div className="flex items-center justify-between rounded border border-slate-800 bg-slate-950/40 p-3">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-cyan-400" />
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-300">Email alerts (Resend)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] text-slate-500">{st.enabled ? "ON" : "OFF"}</span>
          <Switch data-testid="notify-enabled-switch" checked={!!st.enabled} onCheckedChange={(v) => save(v)} />
        </div>
      </div>
      <p className="text-[11px] text-slate-500">
        Emails are sent from <span className="font-mono text-slate-300">pricealert@&lt;your-domain&gt;</span> using your own Resend key.
        The domain must be verified in your Resend account.
      </p>
      <div className="space-y-1">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Resend API key {st.configured && <span className="text-emerald-400">(set — leave blank to keep)</span>}</div>
        <Input data-testid="notify-resend-key-input" type="password" placeholder="re_..." value={f.resend_key}
          onChange={(e) => setF({ ...f, resend_key: e.target.value })} className="border-slate-800 bg-slate-900 font-mono" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Sending domain (verified in Resend)</div>
          <Input data-testid="notify-domain-input" placeholder="alerts.mydesk.com" value={f.from_domain}
            onChange={(e) => setF({ ...f, from_domain: e.target.value })} className="border-slate-800 bg-slate-900 font-mono" />
          {f.from_domain && <div className="font-mono text-[10px] text-slate-500">from: pricealert@{f.from_domain}</div>}
        </div>
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Send alerts to</div>
          <Input data-testid="notify-email-input" placeholder="you@company.com" value={f.alert_email}
            onChange={(e) => setF({ ...f, alert_email: e.target.value })} className="border-slate-800 bg-slate-900 font-mono" />
        </div>
      </div>
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Alert on</div>
        <div className="grid gap-2 sm:grid-cols-2">
          {st.available_kinds.map((k) => (
            <label key={k} data-testid={`notify-kind-${k}`} className="flex cursor-pointer items-center gap-2 rounded border border-slate-800 bg-slate-950/40 px-2.5 py-1.5 text-xs text-slate-300">
              <Checkbox checked={kinds.includes(k)} onCheckedChange={() => toggleKind(k)} />
              {LABELS[k] || k}
            </label>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="notify-save-button" onClick={() => save(st.enabled)} disabled={!!busy} className="bg-cyan-500 font-semibold text-slate-950 hover:bg-cyan-400">
          {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Save</>}
        </Button>
        <Button data-testid="notify-test-button" onClick={test} disabled={!!busy} variant="outline" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
          {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1.5 h-4 w-4" /> Save & send test</>}
        </Button>
      </div>
    </div>
  );
};
